import React, { type FunctionComponent, type ReactNode, useEffect, useState } from 'react'
import { SchemeAppLogo } from './SchemeAppLogo'
import { ArrowRightIcon } from '../../../icons'
import { Link } from 'react-router-dom'
import { campaignRunningStateMap } from './charityCampaignMap'
import { charityStatusMap } from '../../../../lib/charity'
import { Badge } from '@tomra/design-system-react/badge'
type Props = {
  charity: CharitySummaryType & { campaigns: CampaignSummaryType[] }
  AddNote: ReactNode
}

export const ActionListItem: FunctionComponent<Props> = ({ charity, AddNote }) => {
  const { appContext } = charity

  const [newCampaigns, setNewCampaigns] = useState<CampaignSummaryType[]>([])
  const [changedCampaigns, setChangedCampaigns] = useState<CampaignSummaryType[]>([])

  const label = charity.displayName
  const createdAtLabel = Object.hasOwn(charity, 'pendingCharityCreatedAt') ? 'Updated' : 'Created'
  const subLabel = charity.organizationName

  const hasChanges = Object.hasOwn(charity, 'pendingCharityCreatedAt')
  const isNew = !hasChanges && charity.status === 'NEW'
  const newWithChanges = hasChanges && charity.status === 'NEW'

  const createdAt = hasChanges ? charity?.pendingCharityCreatedAt : charity?.createdAt

  useEffect(() => {
    setNewCampaigns(
      charity.campaigns.filter(
        campaign => campaign.status === 'NEW' && !Object.hasOwn(campaign, 'pendingCampaignCreatedAt')
      )
    )
    setChangedCampaigns(charity.campaigns.filter(campaign => Object.hasOwn(campaign, 'pendingCampaignCreatedAt')))
  }, [charity.campaigns])

  return (
    <li className="px-lg py-md" aria-label={`${charity.status} : ${label}`}>
      <div className="grid grid-cols-12 gap-x-md gap-y-sm items-center">
        <Link to={`/charities/${charity.id}`}>
          <Badge variant={hasChanges || isNew ? 'warning' : 'default'}>
            {newWithChanges
              ? 'New charity w/changes'
              : isNew
                ? 'New charity'
                : hasChanges
                  ? 'Changed charity'
                  : `${charityStatusMap[charity.status]} charity`}
          </Badge>
        </Link>

        <div className="row-start-1 col-span-2 col-start-10 flex items-center justify-end">{AddNote}</div>

        <div className="row-start-1 row-span-2 col-start-1 col-span-1">
          <SchemeAppLogo appContext={appContext} />
        </div>

        <div className="row-start-2 col-span-5 pl-sm flex flex-col items-start space-y-xs">
          <span>{label}</span>
          <span className="text-sm">{subLabel}</span>
        </div>

        <div className="row-start-2 col-start-7 col-span-2 flex flex-col items-start justify-center space-y-xs h-full">
          <div className="text-storm text-sm">Campaign status</div>

          {newCampaigns.length === 0 && changedCampaigns.length === 0 && (
            <div className="col-span-1 row-start-2">
              <div className="col-start-2 col-span-11 font-bold text-storm text-sm">
                {campaignRunningStateMap[charity.campaignRunState] || 'No changes'}
              </div>
            </div>
          )}

          {newCampaigns.length > 0 && (
            <div className="col-span-1 row-start-2">
              {newCampaigns.length === 1 ? (
                <Link to={`/charities/${charity.id}?campaignId=${newCampaigns[0].id}`}>
                  <Badge variant="warning">New campaign</Badge>
                </Link>
              ) : (
                <div className="text-sm tag tag-orange">{newCampaigns.length} new campaigns</div>
              )}
            </div>
          )}

          {changedCampaigns.length > 0 && (
            <div className="col-span-1 row-start-2">
              {changedCampaigns.length === 1 ? (
                <Link
                  to={`/charities/${charity.id}?campaignId=${changedCampaigns[0]?.id}`}
                  className="font-bold text-storm text-sm tag tag-orange"
                >
                  Changed campaign
                </Link>
              ) : (
                <div className="font-bold text-storm text-sm tag tag-orange">
                  {changedCampaigns.length} changes campaigns
                </div>
              )}
            </div>
          )}
        </div>

        <div className="row-start-2 col-span-2 col-start-10">
          <div className="text-right text-sm text-storm">{createdAtLabel}</div>
          <div className="text-right">
            {new Date(createdAt as string).toLocaleDateString([], {
              localeMatcher: 'best fit',
              year: '2-digit',
              month: '2-digit',
              day: '2-digit'
            })}
          </div>
        </div>

        <div className="row-start-1 row-span-2 col-span-1 col-start-12 flex justify-end">
          <Link
            to={`/charities/${charity.id}`}
            aria-label={`View ${charity.displayName} details`}
            className="pl-lg py-md"
          >
            <ArrowRightIcon width="1.5rem" height="1.5rem" />
          </Link>
        </div>
      </div>
    </li>
  )
}
